<script setup lang="ts">
import  { type PropType } from 'vue'

import { type Image } from '@/types/image'

import { type Product, type ProductVariant, type ProductVariantSelectedOption } from '@/types'

import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
} from '@headlessui/vue'

import {
  MinusSmallIcon,
  PlusSmallIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true    
  },
  productImages: {
    type: Array as PropType<Image[]>,
    required: true 
  },
  productVariants: {
    type: Array as PropType<ProductVariant[]>,
    required: true    
  },
  selectedProductVariant: {
    type: Object as PropType<ProductVariant | undefined>,
    required: true    
  },
  options: {
    type: Array as PropType<ProductVariantSelectedOption[]>,
    required: true
  },
  isVariant: {
    type: Boolean as PropType<boolean>,
    required: true    
  }
})

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'on:product-variant-change', id: string): void
}>()

const product = toRef(props, 'product')

const { careInstructions } = useProductDetail(product)

useProductJSONLD(product)

// Product Extra Details

const productDetails = [
  careInstructions.value ? {
    name: 'Care Instructions',
    value: careInstructions.value,
  } : null,
]

// Product Variant Color:

const color = ref(props.selectedProductVariant?.id || '')

watch(color, () => {
  emit('on:product-variant-change', color.value)
})

const colorOptions = props.options.filter((option: ProductVariantSelectedOption) => option.name === 'Color')

// Product Variant Size:

const size = ref(props.selectedProductVariant?.id || '')

watch(size, () => {
  emit('on:product-variant-change', size.value)
})

const sizeOptions = props.options.filter((option: ProductVariantSelectedOption) => option.name === 'Size')

// Quantity & Stock Levels:

const quantity = ref(1)

const { getProductVariantQuantityById } = useCartStore()

const selectedProductVariantInventoryLevel = computed<number>(() => {
  if (!props.selectedProductVariant) return 0
  return props.selectedProductVariant.inventoryQuantity - getProductVariantQuantityById(props.selectedProductVariant.id)
})

const price = computed(() => {
  if (!props.selectedProductVariant) return 0
  return typeof props.selectedProductVariant.price === 'string' ? props.selectedProductVariant.price : props.selectedProductVariant.price.amount 
})
</script>

<template>
  <!-- Start Product -->
  <section
    typeof="schema:Product" 
    vocab="https://schema.org/"
    itemtype="https://schema.org/Product" 
    itemscope
    class="lg:grid lg:grid-cols-2 lg:gap-x-8 lg:items-start"
  >
    <!-- Start Product Image Gallery -->
    <TabGroup as="div" class="w-full flex flex-col md:flex-col-reverse">
      <TabPanels class="w-full aspect-w-1 aspect-h-1">
        <TabPanel 
          v-for="image in productImages" 
          :key="image.id"
        >
          <img 
            itemprop="image" 
            property="image"
            :src="image.url"
            :alt="image.altText" 
            class="object-cover object-top w-full h-full bg-gray-100 bg-cover bg-top rounded-tl-none rounded-b-4xl rounded-tr-4xl overflow-hidden"
            decoding="async"
            fetchpriority="high"
          />
        </TabPanel>
      </TabPanels>
      
      <!-- Start Product Image Selector -->
      <div class="w-full max-w-2xl mx-auto mt-6 sm:block lg:max-w-none">
        <TabList class="grid grid-cols-4 gap-6">
          <Tab 
            v-for="image in productImages"
            :key="image.id" 
            class="relative flex items-center justify-center aspect-w-1 aspect-h-1 text-sm font-medium text-gray-900 uppercase bg-white rounded-md cursor-pointer hover:bg-gray-50 focus:outline-none focus:ring focus:ring-offset-4 focus:ring-opacity-50" 
            v-slot="{ selected }"
          >
            <span class="sr-only">
              {{ image.altText }}
            </span>
            <span class="absolute inset-0 overflow-hidden rounded-md">
              <img 
                :src="image.url" 
                :alt="image.altText" 
                class="object-cover object-center w-full h-full"
                decoding="async"
                fetchpriority="low"
              />
            </span>
            <span 
              :class="[selected ? 'ring-blue-500' : 'ring-transparent', 'absolute inset-0 rounded-md ring-2 ring-offset-2 pointer-events-none']" 
              aria-hidden="true" 
            />
          </Tab>
        </TabList>
      </div>
      <!-- End Product Image Selector -->
    </TabGroup>
    <!-- End Product Image Gallery -->

    <!-- Start Product Information -->
    <div 
      class="md:px-4 mt-10 sm:px-0 sm:mt-16 lg:mt-0"
    >
      <h1 
        itemprop="name"
        property="name"
        class="text-2xl font-semibold text-blue-600"
      >
        {{ product.title }}
      </h1>

      <div class="mt-3">
        <h2 class="sr-only">
          Product Price
        </h2>
        <p class="text-xl text-blue-400">
          £{{ price }}
        </p>
      </div>

      <!-- Start Product Quantity & Options Select Form -->
      <form class="mt-6">
        <!-- Start Product Selections -->
        <div 
          class="grid grid-cols-2 md:grid-cols-8 gap-x-1"
        >
          <!-- Start Product Size Option To Purchase Select -->
          <div 
            v-if="sizeOptions.length > 0"
            class="col-span-1 md:col-span-3"
          >
            <label 
              for="purchaseSize" 
              class="block text-sm font-medium text-gray-700"
            >
              Size
            </label>
            <select 
              id="purchaseSize" 
              name="purchaseSize" 
              class="block w-full py-3 pl-3 pr-10 mt-2 text-base border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              v-model="size"
            >
              <option selected disabled>
                Pick a size...
              </option>
              <option 
                v-for="size in sizeOptions" 
                :key="size.value"
                :value="size.id"
              >
                {{ size.value }}
              </option>
            </select>
          </div>
          <!-- End Product Size Option To Purchase Select -->

          <!-- Start Product Colour Option To Purchase Select -->
          <div 
            v-if="colorOptions.length > 0"
            class="col-span-1 md:col-span-3"
          >
            <label 
              for="purchaseColor" 
              class="block text-sm font-medium text-gray-700"
            >
              Colour
            </label>
            <select 
              id="purchaseColor" 
              name="purchaseColor" 
              class="block w-full py-3 pl-3 pr-10 mt-2 text-base border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              v-model="color"
            >
              <option selected disabled>
                Pick a colour...
              </option>
              <option 
                v-for="color in colorOptions" 
                :key="color.value"
                :value="color.id"
              >
                {{ color.value }}
              </option>
            </select>
          </div>
          <!-- End Product Colour Option To Purchase Select -->

          <!-- Start Quantity To Purchase Select -->
          <div class="col-span-1 md:col-span-2">
            <label 
              for="purchaseQuantity" 
              class="block text-sm font-medium text-gray-700" 
              :data-total-quantity="selectedProductVariant?.inventoryQuantity || 0"
            >
              Qty
            </label>
            <select 
              id="purchaseQuantity" 
              name="purchaseQuantity" 
              class="block w-full md:w-16 py-3 pl-3 pr-10 mt-2 text-base hover:bg-blue-100 border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm cursor-pointer"
              v-model="quantity"
            >
              <option selected disabled>0</option>
              <option
                v-for="n in selectedProductVariantInventoryLevel"
                :key="n"
                :value="n"
              >
                {{ n }}
              </option>
            </select>
          </div>
          <!-- End Quantity To Purchase Select -->
        </div>
        <!--  End Product Selections -->

        <!-- Start Product Detail Actions -->
        <div 
          class="flex mt-6 sm:flex-col1"
        >
          <AddToCartSingular 
            v-if="selectedProductVariant"
            :product="product"
            :product-variant="selectedProductVariant"
            :quantity="quantity"
            :quantity-available="selectedProductVariantInventoryLevel"
          />

          <AddToFavourites 
            v-if="selectedProductVariant"
            :product="product"
            :product-variant="selectedProductVariant"
          />
        </div>
        <!-- End Product Detail Actions -->
      </form>
      <!-- End Product Quantity Select Form -->

      <div class="mt-10">
        <h2 class="sr-only">
          Description
        </h2>
        <div 
          itemprop="description"
          property="description"
          class="prose-md prose text-gray-600" 
          v-html="product.descriptionHtml" 
        />
      </div>

      <section aria-labelledby="details-heading" class="mt-12">
        <h2 id="details-heading" class="sr-only">
          Additional Details
        </h2>

        <div class="border-t divide-y divide-blue-300">
          <Disclosure 
            as="div" 
            :key="'Delivery Information'" 
            v-slot="{ open }"
          >
            <h3>
              <DisclosureButton class="relative flex items-center justify-between w-full py-6 text-left group">
                <span :class="[open ? 'text-blue-600' : 'text-gray-900', 'text-sm font-medium']">
                  Delivery Information
                </span>
                <span class="flex items-center ml-6">
                  <PlusSmallIcon v-if="!open" class="block w-6 h-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                  <MinusSmallIcon v-else class="block w-6 h-6 text-blue-400 group-hover:text-blue-500" aria-hidden="true" />
                </span>
              </DisclosureButton>
            </h3>
            <div v-show="open">
              <DisclosurePanel static as="div" class="pb-6 prose-sm prose prose-gray-400">
                <DeliveryInformation />
              </DisclosurePanel>
            </div>
          </Disclosure>
          <Disclosure 
            as="div" 
            :key="'Returns Policy'" 
            v-slot="{ open }"
          >
            <h3>
              <DisclosureButton class="relative flex items-center justify-between w-full py-6 text-left group">
                <span :class="[open ? 'text-blue-600' : 'text-gray-900', 'text-sm font-medium']">
                  Returns Policy
                </span>
                <span class="flex items-center ml-6">
                  <PlusSmallIcon v-if="!open" class="block w-6 h-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                  <MinusSmallIcon v-else class="block w-6 h-6 text-blue-400 group-hover:text-blue-500" aria-hidden="true" />
                </span>
              </DisclosureButton>
            </h3>
            <div v-show="open">
              <DisclosurePanel static as="div" class="pb-6 prose-sm prose prose-gray-400">
                <ReturnsPolicy />
              </DisclosurePanel>
            </div>
          </Disclosure>
          <Disclosure as="div" v-for="detail in productDetails.filter(d => d)" :key="detail?.name" v-slot="{ open }">
            <h3>
              <DisclosureButton class="relative flex items-center justify-between w-full py-6 text-left group">
                <span :class="[open ? 'text-blue-600' : 'text-gray-900', 'text-sm font-medium']">
                  {{ detail?.name }}
                </span>
                <span class="flex items-center ml-6">
                  <PlusSmallIcon v-if="!open" class="block w-6 h-6 text-gray-400 group-hover:text-gray-500" aria-hidden="true" />
                  <MinusSmallIcon v-else class="block w-6 h-6 text-blue-400 group-hover:text-blue-500" aria-hidden="true" />
                </span>
              </DisclosureButton>
            </h3>
            <DisclosurePanel as="div" class="pb-6 prose-sm prose prose-gray-400">
              <div v-html="detail?.value"></div>
            </DisclosurePanel>
          </Disclosure>
        </div>
      </section>
    </div>
    <!-- End Product Information -->
  </section>
  <!-- End Product -->
</template>